import { ResponsiveBar } from '@nivo/bar';
import { Typography, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { formatDollar, formatDollarShort } from '../utilityFunctions';

// Styled components for better performance
const StyledTooltip = styled('div')(({ theme }) => ({
  padding: '4px 6px',
  backgroundColor: theme.palette.grey[800],
  color: theme.palette.common.white,
  borderRadius: '6px',
  fontFamily: 'Titillium Web, sans-serif',
  boxShadow: theme.shadows[4],
  minWidth: '180px',
  whiteSpace: 'nowrap',
}));

const ChartContainer = styled(Box)(({ theme }) => ({
  height: 250,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const NoDataTypography = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  color: theme.palette.text.secondary,
}));

const CostsVsBillingsChart = ({ invoiceData, budgetData }) => {
    // Extract the latest cumulative_paid and cumulative_invoiced from invoiceData
    const latestInvoice = invoiceData && invoiceData.length > 0 ? invoiceData[invoiceData.length - 1] : null;
    console.log('--- DEBUG: invoiceData ---');
    console.log(invoiceData);

    const currentContractAmount = budgetData?.current_contract_summary?.revised_contract_value || 0;
    const cumulativePaid = latestInvoice ? latestInvoice.cumulative_paid : 0;
    const cumulativeInvoiced = latestInvoice ? latestInvoice.cumulative_invoiced : 0;
    
    // "Billed but Not Paid" - invoices that have been sent but not yet paid
    const billedButNotPaid = cumulativeInvoiced - cumulativePaid;
    const paymentToGo = currentContractAmount - cumulativeInvoiced;

    // Extract the JTD costs from profitData
    const jtdCosts = budgetData?.current_contract_summary?.jtd_costs || 0;
    const costs = budgetData?.current_contract_summary?.total_estimated_costs || 0;
    const costsToGo = costs - jtdCosts
 
  // 2) Build Data for Bars
  const chartData = [
    {
      category: 'Billings',
      cumulativePaid: cumulativePaid,
      billedButNotPaid: billedButNotPaid,
      paymentToGo: paymentToGo,
      jtdCosts: 0,
      costsToGo: 0,
    },
    {
      category: 'Costs',
      cumulativePaid: 0,
      billedButNotPaid: 0,
      paymentToGo: 0,
      jtdCosts: jtdCosts,
      costsToGo: costsToGo,
    },
  ];

  // 3) Define Stacked Keys
  const keys = ['cumulativePaid', 'billedButNotPaid', 'paymentToGo', 'jtdCosts', 'costsToGo'];

  // 4) Color Map Keys
  const colorByKey = {
    cumulativePaid: '#083d61',
    billedButNotPaid: '#FFC107',
    paymentToGo: '#3377a7',
    jtdCosts: '#54bef3',
    costsToGo: '#89d4f7',
  };

  const labelByKey = {
    cumulativePaid: 'Cumulative Paid',
    billedButNotPaid: 'Billed but Not Paid',
    paymentToGo: 'Remaining Billings',
    jtdCosts: 'JTD Costs',
    costsToGo: 'Remaining Costs',
  }

  // 5) Render
  return (
    <ChartContainer>
      {cumulativePaid === 0 && jtdCosts === 0 ? (
        <NoDataTypography variant="body1">
          No data available
        </NoDataTypography>
      ) : (
        <ResponsiveBar
          data={chartData}
          keys={keys}
          indexBy="category"
          margin={{ top: 60, right: 50, bottom: 60, left: 120 }}
          padding={0.5}
          colors={({ id }) => colorByKey[id] || '#ccc'}
          names={({ id }) => labelByKey[id] || 'N/A'}
          borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
          borderRadius={1}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: '',
            legendPosition: 'middle',
            legendOffset: 36,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Amount ($)',
            legendPosition: 'middle',
            legendOffset: -85,
            format: value => formatDollarShort(value),
          }}
          // Avoid label overlap for small bars
          labelSkipWidth={42}
          labelSkipHeight={25}
          labelTextColor={{ from: 'color', modifiers: [['brighter', 10]] }}
          label={d => formatDollarShort(d.value)}
          // Add legends with billing data on left and cost data on right
          legends={[
            // Left side: All billing data
            {
              dataFrom: 'keys',
              anchor: 'top-left',
              direction: 'column',
              translateX: 20,
              translateY: -60,
              itemsSpacing: 2,
              itemWidth: 140,
              itemHeight: 15,
              itemDirection: 'left-to-right',
              symbolSize: 12,
              fontSize: "20px",
              symbolShape: 'square',
              itemTextColor: '#000',
              data: [
                { id: 'billedButNotPaid', label: 'Billed but Not Paid', color: colorByKey.billedButNotPaid },{ id: 'cumulativePaid', label: 'Cumulative Paid', color: colorByKey.cumulativePaid },
                { id: 'paymentToGo', label: 'Remaining Billings', color: colorByKey.paymentToGo },
              ],
            },
            // Right side: All cost data
            {
              dataFrom: 'keys',
              anchor: 'top-right',
              direction: 'column',
              translateX: -5,
              translateY: -50,
              itemsSpacing: 2,
              itemWidth: 140,
              itemHeight: 15,
              itemDirection: 'left-to-right',
              symbolSize: 12,
              symbolShape: 'square',
              itemTextColor: '#000',
              data: [
                { id: 'jtdCosts', label: 'JTD Costs', color: colorByKey.jtdCosts },
                { id: 'costsToGo', label: 'Remaining Costs', color: colorByKey.costsToGo },
              ],
            },
          ]}
          // Theme styling
          theme={{
            fontFamily: 'Titillium Web, sans-serif',
            legends: {
              text: {
                fontSize: 13,
                fontFamily: 'Titillium Web, sans-serif',
              }
            },
            labels: {
              text: {
                fontSize: 11,
                fill: '#000000',
                fontFamily: 'Titillium Web, sans-serif',
              },
            },
            axis: {
              ticks: {
                text: {
                  fontSize: 14,
                  fill: '#555555',
                  fontFamily: 'Titillium Web, sans-serif',
                },
              },
              legend: {
                text: {
                  fontSize: 17,
                  fill: '#333333',
                  fontFamily: 'Titillium Web, sans-serif',
                },
              },
            },
          }}
          animate={true}
          motionStiffness={90}
          motionDamping={15}
          // Custom tooltip using styled component
          tooltip={({ id, value, indexValue }) => (
            <StyledTooltip>
              <strong>{`${indexValue}: ${labelByKey[id]}`}</strong>
              <br />
              Amount: {formatDollar(value)}
            </StyledTooltip>
          )}
        />
      )}
    </ChartContainer>
  );
};

// PropTypes for better type checking
CostsVsBillingsChart.propTypes = {
  invoiceData: PropTypes.arrayOf(
    PropTypes.shape({
      cumulative_paid: PropTypes.number,
      cumulative_invoiced: PropTypes.number,
    })
  ),
  budgetData: PropTypes.shape({
    current_contract_summary: PropTypes.shape({
      revised_contract_value: PropTypes.number,
      jtd_costs: PropTypes.number,
      total_estimated_costs: PropTypes.number,
    }),
  }),
};

// Default props
CostsVsBillingsChart.defaultProps = {
  invoiceData: [],
  budgetData: null,
};

export default CostsVsBillingsChart;
